// ValidateLogicSection.js
import React, { useState } from 'react';
import { Check, Plus, Trash2 } from 'lucide-react';

const ValidateLogicSection = ({ 
  formData, 
  validationErrors, 
  airtableBases, 
  airtableTables, 
  airtableFields,
  isLoadingBases,
  isLoadingTables,
  isLoadingFields,
  toggleSection,
  updateValidateBase,
  updateValidateTable,
  updateValidateField,
  addValidationCondition,
  removeValidationCondition,
  updateValidationCondition
}) => {

  // Validate Logic handlers
  const handleValidateBaseChange = (e) => {
    const baseId = e.target.value;
    const baseName = e.target.options[e.target.selectedIndex].text;
    updateValidateBase(baseId, baseName);
  };

  const handleValidateTableChange = (e) => {
    const tableId = e.target.value;
    const tableName = e.target.options[e.target.selectedIndex].text;
    updateValidateTable(tableId, tableName);
  };

  const handleValidateFieldChange = (e) => {
    const fieldId = e.target.value;
    const fieldName = e.target.options[e.target.selectedIndex].text;
    updateValidateField(fieldId, fieldName);
  };


  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium flex items-center">
          <span className={`mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full ${formData.hasValidateLogic ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-400'}`}>
            <Check className="h-4 w-4" />
          </span>
          Validate Logic
        </h3>
        <div className="relative inline-block w-10 mr-2 align-middle select-none">
          <input
            type="checkbox"
            id="toggle-validate"
            className="sr-only"
            checked={formData.hasValidateLogic}
            onChange={toggleSection}
          />
          <label 
            htmlFor="toggle-validate"
            className={`block overflow-hidden h-6 rounded-full cursor-pointer ${formData.hasValidateLogic ? 'bg-indigo-500' : 'bg-gray-300'}`}
          >
            <span className={`absolute block h-4 w-4 rounded-full bg-white top-1 transition-transform duration-200 ease-in-out ${formData.hasValidateLogic ? 'right-1 transform translate-x-0' : 'left-1 transform translate-x-0'}`}></span>
          </label>
        </div>
      </div>
      
      {formData.hasValidateLogic && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Configure how to look up and validate QR codes in Airtable.
          </p>
          
          {/* Airtable Connection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Airtable Base</label>
              <div className="relative">
                <select
                  value={formData.validate_logic.search_base.id}
                  onChange={handleValidateBaseChange}
                  disabled={isLoadingBases}
                  className={`w-full px-3 py-2 border ${validationErrors.validateBase ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${isLoadingBases ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <option value="">{isLoadingBases ? 'Loading bases...' : 'Select a base'}</option>
                  {airtableBases.map(base => (
                    <option key={base.id} value={base.id}>{base.name}</option>
                  ))}
                </select>
                {isLoadingBases && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg className="animate-spin h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              {validationErrors.validateBase && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.validateBase}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Table</label>
              <div className="relative">
                <select
                  value={formData.validate_logic.search_table.id}
                  onChange={handleValidateTableChange}
                  disabled={!formData.validate_logic.search_base.id || isLoadingTables}
                  className={`w-full px-3 py-2 border ${validationErrors.validateTable ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${(!formData.validate_logic.search_base.id || isLoadingTables) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <option value="">
                    {!formData.validate_logic.search_base.id 
                      ? 'Select a base first'
                      : isLoadingTables 
                        ? 'Loading tables...' 
                        : 'Select a table'
                    }
                  </option>
                  {airtableTables.map(table => (
                    <option key={table.id} value={table.id}>{table.name}</option>
                  ))}
                </select>
                {isLoadingTables && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg className="animate-spin h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              {validationErrors.validateTable && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.validateTable}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lookup Field</label>
              <div className="relative">
                <select
                  value={formData.validate_logic.search_field.id}
                  onChange={handleValidateFieldChange}
                  disabled={!formData.validate_logic.search_table.id || isLoadingFields}
                  className={`w-full px-3 py-2 border ${validationErrors.validateField ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${(!formData.validate_logic.search_table.id || isLoadingFields) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <option value="">
                    {!formData.validate_logic.search_table.id 
                      ? 'Select a table first'
                      : isLoadingFields 
                        ? 'Loading fields...' 
                        : 'Select a field'
                    }
                  </option>
                  {airtableFields.map(field => (
                    <option key={field.id} value={field.id}>{field.name}</option>
                  ))}
                </select>
                {isLoadingFields && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg className="animate-spin h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              {validationErrors.validateField && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.validateField}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">The field to match the scanned QR code value against.</p>
            </div>
          </div>
          
          {/* Validation Conditions */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Validation Conditions</h4>
            <p className="text-sm text-gray-500 mb-4">
              Define conditions for the scan to be considered valid.
            </p>
            
            {formData.validate_logic.search_table.id ? (
              <ValidationConditions
                airtableFields={airtableFields}
                isLoadingFields={isLoadingFields}
                formData={formData}
                addValidationCondition={addValidationCondition}
                updateValidationCondition={updateValidationCondition}
                removeValidationCondition={removeValidationCondition}
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Select a table first to configure validation conditions.</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {!formData.hasValidateLogic && (
        <p className="text-sm text-gray-500">
          Enable this section to validate QR codes against Airtable records.
        </p>
      )}
    </div>
  );
};

// Validation conditions component matching real Supabase structure
const ValidationConditions = ({ 
  airtableFields, 
  isLoadingFields, 
  formData, 
  addValidationCondition, 
  updateValidationCondition, 
  removeValidationCondition 
}) => {
  // Get the actual data structure from Supabase
  const conditionGroups = formData.validate_logic.condition_groups || [];
  const standaloneGroup = conditionGroups.find(g => g.isStandalone) || { conditions: [] };
  const orGroup = conditionGroups.find(g => g.operator === 'OR') || { conditions: [] };
  
  // Extract conditions arrays - matching real structure
  const allConditions = standaloneGroup.conditions || [];
  const anyConditions = orGroup.conditions || [];

  // New condition states for both sections
  const [newAllCondition, setNewAllCondition] = useState({
    field: { id: '', name: '' },
    operator: 'is',
    value: '',
    error_message: ''
  });

  const [newAnyCondition, setNewAnyCondition] = useState({
    field: { id: '', name: '' },
    operator: 'is',
    value: '',
    error_message: ''
  });

  const handleFieldChange = (section, fieldId, fieldName) => {
    const setter = section === 'all' ? setNewAllCondition : setNewAnyCondition;
    setter(prev => ({
      ...prev,
      field: { id: fieldId, name: fieldName }
    }));
  };

  const handleOperatorChange = (section, operator) => {
    const setter = section === 'all' ? setNewAllCondition : setNewAnyCondition;
    setter(prev => ({
      ...prev,
      operator
    }));
  };

  const handleValueChange = (section, value) => {
    const setter = section === 'all' ? setNewAllCondition : setNewAnyCondition;
    setter(prev => ({
      ...prev,
      value
    }));
  };

  const handleErrorMessageChange = (section, error_message) => {
    const setter = section === 'all' ? setNewAllCondition : setNewAnyCondition;
    setter(prev => ({
      ...prev,
      error_message
    }));
  };

  const handleAddCondition = (section) => {
    const condition = section === 'all' ? newAllCondition : newAnyCondition;
    const setter = section === 'all' ? setNewAllCondition : setNewAnyCondition;
    
    // Don't add if field is not selected
    if (!condition.field.id) return;
    
    // Don't add if value is required but empty (for non-empty checks)
    if (['is', 'is_not', 'contains', 'does_not_contain'].includes(condition.operator) && !condition.value) return;
    
    // Add condition - simplified to match real structure
    if (section === 'all') {
      // Add to standalone group (isStandalone: true)
      addValidationCondition(condition, 'standalone');
    } else {
      // Add to OR group for "any" conditions
      addValidationCondition(condition, 'or');
    }
    
    // Reset the form
    setter({
      field: { id: '', name: '' },
      operator: 'is',
      value: '',
      error_message: ''
    });
  };

  const renderConditionForm = (section, condition, setter) => (
    <div className="space-y-3 mt-2">
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-3">
          <select
            value={condition.field.id}
            onChange={(e) => {
              const fieldId = e.target.value;
              const fieldName = e.target.options[e.target.selectedIndex]?.text || '';
              handleFieldChange(section, fieldId, fieldName);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm"
            disabled={isLoadingFields}
          >
            <option value="">Select field</option>
            {airtableFields.map(field => (
              <option key={field.id} value={field.id}>{field.name}</option>
            ))}
          </select>
        </div>
        <div className="col-span-3">
          <select
            value={condition.operator}
            onChange={(e) => handleOperatorChange(section, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm"
          >
            <option value="is">is</option>
            <option value="is_not">is not</option>
            <option value="contains">contains</option>
            <option value="does_not_contain">does not contain</option>
            <option value="is_empty">is empty</option>
            <option value="is_not_empty">is not empty</option>
          </select>
        </div>
        <div className="col-span-3">
          <input
            type="text"
            value={condition.value}
            onChange={(e) => handleValueChange(section, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            placeholder="Enter value"
            disabled={['is_empty', 'is_not_empty'].includes(condition.operator)}
          />
        </div>
        <div className="col-span-3">
          <input
            type="text"
            value={condition.error_message}
            onChange={(e) => handleErrorMessageChange(section, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            placeholder="Error message (optional)"
          />
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => handleAddCondition(section)}
          disabled={!condition.field.id || (['is', 'is_not', 'contains', 'does_not_contain'].includes(condition.operator) && !condition.value)}
          className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add condition
        </button>
      </div>
    </div>
  );

  const renderConditionList = (conditions, title, section) => (
    <div className="border border-gray-200 rounded-lg p-4">
      <h5 className="font-medium text-gray-900 mb-3">{title}</h5>
      
      {conditions.length === 0 ? (
        <p className="text-sm text-gray-500 italic mb-3">No conditions added yet</p>
      ) : (
        <div className="space-y-2 mb-3">
          {conditions.map((condition, index) => {
            const fieldName = condition.field?.name || 'Unknown field';
            const operator = condition.operator || 'is';
            const value = condition.value || '';
            const errorMessage = condition.error_message || '';
            
            return (
              <div key={index} className="bg-gray-50 p-3 rounded">
                <div className="flex items-center justify-between">
                  <span className="text-sm">
                    <strong>{fieldName}</strong> {operator} {value || (operator.includes('empty') ? '' : '[empty]')}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeValidationCondition(section === 'all' ? 'standalone' : 'or', index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {errorMessage && (
                  <div className="mt-1 text-xs text-gray-600 italic">
                    Error: "{errorMessage}"
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {renderConditionForm(section, section === 'all' ? newAllCondition : newAnyCondition)}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Add conditions to validate scanned QR codes against existing Airtable records. 
              Use "Apply all" when every condition must be true, or "Apply any" when at least one condition needs to be true.
            </p>
          </div>
        </div>
      </div>
      
      {renderConditionList(allConditions, 'Apply all of the following', 'all')}
      {renderConditionList(anyConditions, 'Apply any of the following', 'any')}
    </div>
  );
};

export default ValidateLogicSection;